<script>
  import { createEventDispatcher, onMount, getContext } from 'svelte';
  import { fade } from 'svelte/transition';
  import tippy from 'tippy.js';
  import { goto } from '$app/navigation';

  import Alert from '@components/Alert.svelte';
  import Text from '@components/Text.svelte';
  import VisibilityGuard from '@components/VisibilityGuard.svelte';
  import EmojiPicker from '@app-components/EmojiPicker.svelte';
  import ListAuthors from '@app-components/ListAuthors.svelte';
  import DuplicateListModal from '@app-components/modal/DuplicateListModal.svelte';
  import ListSettingsModal from '@app-components/modal/ListSettingsModal.svelte';
  import MoveToCollectionModal from '@app-components/modal/MoveToCollectionModal.svelte';
  import ShareModal from '@app-components/modal/ShareModal.svelte';
  import Notification from '@utils/notification';
  import { getCurrentUserStore } from '@stores/current_user';
  import GroupAjax from '@ajax/group';
  import WelcomeNoteActiveIndicator from '../WelcomeNoteActiveIndicator.svelte';
  import TileCoverPhotos from './TileCoverPhotos.svelte';
  import ChangeCoverPhotosModal from '@app-components/modal/ChangeCoverPhotosModal.svelte';
  import { browser } from '$app/environment';
  import ScoreStatusBadge from '@components/ScoreStatusBadge.svelte';

  import { getProfileStore } from '@stores/profile';
  import { getGlobalStore } from '@stores/global';

  import 'tippy.js/themes/light.css';
  import ListCopier from '@app-components/group/ListCopier.svelte';
  import GroupActionsBar from '@app-components/group/GroupActionsBar.svelte';
  import HideReportedContentOverlay from '@app-components/report/HideReportedContentOverlay.svelte';

  const dispatch = createEventDispatcher();

  export let id;
  export let user;
  export let groupCollectionId;
  export let mobile = false;
  export let score = null;
  export let showSpotlightTile = false;
  let reportedContentKey = `group_${id}`;
  let reportedContent = getContext('reportedContent');
  $: reportedContentId = $reportedContent[reportedContentKey] || null;
  let currentUser = getCurrentUserStore('default');
  let groupsStore = getProfileStore('groupsStore');
  let groupCollectionStore = getProfileStore('groupCollectionStore');
  let reportContentStore = getGlobalStore('reportContentStore');
  let group = groupsStore.find(id);
  export let groupCollections;

  let showEmoji = false;
  let showOptionsMenu = false;
  let editingText = false;

  let showDeleteModal = false;
  let showDuplicateListModal = false;
  let moveToCollectionModal = false;
  let listSettingsModal = false;
  let showShareModal = false;
  let showCoverPhotosModal = false;

  let settingsSelectedOptionIndex = 1;
  export let showDetailsButton = true;
  export let showWelcomeNoteFlag = true;
  export let bookmarkId = null;

  $: editable = group && group.permissions && group.permissions.edit;
  $: isHidden = group && group.listing_flag == 'unlisted';
  $: isPrivate = group && group.privacy_flag == 'private';
  $: authors = group && group.users.filter((u) => u.role != 'viewer');
  $: owner = group && group.users.filter((u) => u.role == 'admin')[0];

  let openEmojiSelector = function () {
    if (editable && !mobile) {
      showEmoji = true;
    }

    dispatch('emoji-select-clicked', {
      groupId: group.id,
    });
  };

  let closeEmojiSelector = function () {
    showEmoji = false;
    if (bookmarkId == null) {
      dispatch('emoji-select-closed', {
        groupId: group.id,
      });
    }
  };

  let showOptions = function () {
    showOptionsMenu = true;
    if (bookmarkId == null) {
      dispatch('options-select-clicked', {
        groupId: group.id,
      });
    }
  };

  let closeOptions = function () {
    showOptionsMenu = false;
    if (bookmarkId == null) {
      dispatch('options-select-closed', {
        groupId: group.id,
      });
    }
  };

  let displayMoveToBookmarkCollectionModal = function () {
    closeOptions();
    dispatch('move-to-bookmark-collection', { bookmarkId: bookmarkId });
  };

  let displayRemoveBookmarkModal = function () {
    closeOptions();
    dispatch('remove-bookmark', { bookmarkId: bookmarkId });
  };

  let displayShareModal = function () {
    closeOptions();
    showShareModal = true;
  };

  let displayCoverPhotosModal = function () {
    closeOptions();
    showCoverPhotosModal = true;
  };

  let setupTippy = function (node) {
    let tippyInstance = tippy(node);
    return {
      destroy() {
        tippyInstance.destroy();
      },
    };
  };

  let setupTippyForBrokenLink = function (node, targetUrl) {
    var tippyObj;
    var setup = function (targetUrl) {
      if (targetUrl == '' && tippyObj) {
        tippyObj.destroy();
        tippyObj = undefined;
      } else if (targetUrl && targetUrl != '') {
        var content = `<a href="${targetUrl}" style='color: white; word-wrap: break-word;'>Broken links detected!</a>`;

        if (tippyObj) {
          tippyObj.setContent(content);
        } else {
          tippyObj = tippy(node, {
            content,
            allowHTML: true,
            interactive: true,
            placement: 'bottom',
          });
        }
      }
    };

    setup(targetUrl);

    return {
      update(x) {
        setup(x);
      },
    };
  };

  let tileClicked = function (e) {
    if (!editingText) {
      dispatch('tile-clicked', { event: e });
    }
  };

  let selectEmoji = function (emoji) {
    group.emoji.outside_src = emoji.outside_src;
    dispatch('emoji-selected', { emoji: emoji });
  };

  let goToPrivacySettings = function (e) {
    if (editable) {
      e.preventDefault();
      e.stopPropagation();

      if (!mobile) {
        settingsSelectedOptionIndex = 1;
        showListSettingsModal();
      }

      dispatch('list-privacy-clicked');
    }
  };

  let showListSettingsModal = function () {
    closeOptions();
    listSettingsModal = true;
  };

  let showMoveToCollectionModal = function () {
    closeOptions();
    moveToCollectionModal = true;
  };

  let hideListSettingsModal = function () {
    listSettingsModal = false;
  };

  let hideMoveToCollectionModal = function () {
    moveToCollectionModal = false;
  };

  let extractText = function (html) {
    // Remove all HTML tags using a regular expression
    return html.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ');
  };

  onMount(() => {
    let sub = group.subscribe((x) => (group = x));
    let subGC = groupCollectionStore.subscribe((x) => (groupCollections = x));

    return () => {
      sub();
      subGC();
    };
  });
</script>

<style style="text/scss" lang="scss">
  .tile-container {
    position: relative;
    height: inherit;
  }

  .tile {
    color: black;
    position: relative;
    cursor: pointer;
    border-radius: 15px;
    flex-direction: column;
    display: flex;
    background: #fff;
    width: 100%;
    box-shadow: 0 12px 64px -20px rgba(0, 0, 0, 0.2);
    padding-left: 10px;
    padding-right: 10px;
    padding: 20px;
    border: 2px solid transparent;
    transition: 0.25s;
    height: 100%;
    padding-top: 10px !important;

    &:hover {
      box-shadow: 0 1.375rem 6.25rem rgba(0, 0, 0, 0.18);
    }

    .top-bar {
      display: flex;
      justify-content: space-between;
      position: relative;

      .back-btn {
        position: absolute;
        right: -7px;
        top: -5px;
        padding: 10px;
        background: transparent;
        border: none;
        cursor: pointer;
        display: inline-block;

        &:hover {
          path {
            stroke: #a893f9;
          }
        }
      }

      .editable.emoji {
        cursor: pointer;

        &:hover {
          background: #ede9ff;
        }
      }

      .emoji {
        width: 46px;
        height: 46px;
        border-radius: 7px;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: 0.25s;
        margin-top: 10px;

        .icon {
          width: 32px;
          height: 32px;
          position: relative;

          img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            flex-shrink: 0;
            display: inline-block;
          }
        }
      }

      .tile-options {
        display: flex;
        transition: 0.25s;

        .tile-menu {
          padding-top: 10px;
          padding-right: 7px;
          display: inline-block;
          background: transparent;
          border: none;
          cursor: pointer;
          padding-left: 7px;

          // &:hover {
          //   path {
          //     fill: #a893f9 !important;
          //   }
          // }

          // path {
          //   fill: rgb(213, 207, 234);
          // }
        }

        .tile-btn {
          padding-top: 10px;
          margin-right: 0px;
          background: transparent;
          border: none;
          cursor: pointer;
          display: inline-block;

          // svg {
          //   height: 20px;
          //   width: 20px;
          // }
        }
      }
    }

    .main-content {
      margin-left: 0px;
      margin-right: 0px;

      .group-metadata-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        margin-top: -2px;
      }

      .group-name-container {
        height: 60px;
        display: flex;
        flex-direction: column-reverse;

        .group-name {
          word-break: break-word;
          line-height: 1.4;
          font-size: 18px;
          font-weight: 500;
          user-select: text;
        }
      }

      .tile-group-description {
        font-size: 14px;
        line-height: 20px;
        color: #7f7f7f;
        margin-top: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        margin-left: 4px;
        margin-bottom: 6px;
      }

      .items-count {
        font-size: 16px;
        line-height: 20px;
        color: #7f7f7f;
        margin-top: 10px;
        &.bookmarks {
          margin-top: 0px;
        }
      }

      .authors {
        margin-top: 8px;
      }
      .groups-authors {
        margin-top: 24px;
      }
    }

    .tile-group-bottom-bar {
      margin-top: auto;
      width: 100%;
      margin-left: 4px;
      margin-right: 10px;
      padding-top: 10px;
      margin-bottom: -10px;
    }

    &.cover-photos-tile {
      padding-left: 12px;
      padding-right: 12px;
      padding-bottom: 12px;
      padding-top: 6px !important;

      .top-bar {
        justify-content: space-between;
        min-height: 24px;
        padding-bottom: 6px;

        .emoji {
          display: none;
        }
      }

      .group-name-container {
        margin-top: 12px;
        margin-left: 4px;
        width: 100%;
      }

      .group-metadata-container {
        margin-left: 4px;
      }
    }

    &.tile-options-menu {
      cursor: unset;
      z-index: 100;
      position: absolute;
      width: 320px;
      height: min-content;
    }
  }

  .menu-options {
    display: flex;
    flex-direction: column;
    padding: 10px;
    height: min-content;
    margin-bottom: -5px;

    .menu-options-view-list {
      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    * :global(.link),
    .link {
      cursor: pointer;
      font-weight: 500;
      &:hover {
        color: #a893f9;
      }
    }

    .menu-options-privacy {
      margin-top: 20px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-duplicate {
      margin-top: 15px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-members {
      margin-top: 15px;

      a {
        font-weight: 500;

        &:hover {
          color: #a893f9;
        }
      }
    }

    .menu-options-delete {
      margin-top: 15px;

      a {
        font-weight: 500;
        color: #ff5567;

        &:hover {
          color: #99333d;
        }
      }
    }
  }

  .emoji-container {
    position: relative;
    left: 30px;
    top: 75px;
    width: 360px;
    z-index: 99;
  }

  .yes-delete-btn {
    height: 48px;
    color: #ff5567;
    font-weight: 500;
    font-size: 14px;
    border: none;
    border-radius: 15px;
    background: #fde7e7;
    cursor: pointer;
    display: block;
    width: 100%;

    &:hover {
      background: #ff5567;
      color: white;
    }
  }

  .cancel-btn {
    width: 100%;
    color: #000;
    font-size: 14px;
    margin-top: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-weight: 500;
    margin-bottom: -10px;
  }

  .mobile {
    height: 235px;
    width: 300px;
    box-shadow: 0 6px 32px -10px rgba(0, 0, 0, 0.2);

    &.tile-options-menu {
      width: 300px;
    }

    &:hover {
      box-shadow: 0 6px 32px -10px rgba(0, 0, 0, 0.3);
    }

    .group-name {
      font-size: 15px !important;
    }

    .items-count {
      font-size: 14px !important;
    }

    .tile-btn {
      margin-right: 2px !important;
    }

    &.cover-photos-tile {
      height: 100%;
    }
    &.bookmarks {
      height: 140px !important;
    }

    .menu-options {
      height: 100%;
    }
  }
  .left-side {
    margin-left: 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding-top: 0px;
  }
</style>

{#if showDeleteModal}
  <Alert
    mobile={mobile}
    on:yes-click={() => {
      dispatch('delete-clicked');
    }}
    question="Are you sure you want to delete this?"
    yesButtonText="Delete"
    objectText={group.name}
    bind:show={showDeleteModal} />
{/if}

{#if showDuplicateListModal}
  <DuplicateListModal
    mobile={mobile}
    on:success={(e) => {
      dispatch('new-duplicate-created', { group: e.detail.group });
    }}
    bind:showModal={showDuplicateListModal}
    group={group} />
{/if}

{#if listSettingsModal}
  <ListSettingsModal
    mobile={mobile}
    group={group}
    bind:selectedOptionIndex={settingsSelectedOptionIndex}
    showModal={listSettingsModal}
    on:close={() => {
      hideListSettingsModal();
    }}
    on:duplicate={(e) => {
      dispatch('new-duplicate-created', { group: e.detail.group });
    }} />
{/if}

{#if moveToCollectionModal}
  <MoveToCollectionModal
    mobile={mobile}
    groupId={group.id}
    groupCollectionId={groupCollectionId}
    showModal={moveToCollectionModal}
    on:close={() => {
      hideMoveToCollectionModal();
    }} />
{/if}

{#if showShareModal}
  <ShareModal mobile={mobile} bind:showShareModal={showShareModal} slug={group.slug} group={group} />
{/if}

{#if showCoverPhotosModal}
  <ChangeCoverPhotosModal bind:showModal={showCoverPhotosModal} mobile={mobile} group={group} user={user} />
{/if}

<div style={mobile ? 'position: relative' : ''} class="tile-container">
  {#if showOptionsMenu}
    <div class="backdrop" on:click|preventDefault|stopPropagation={closeOptions} in:fade={{ duration: 100 }} />

    <div
      class="tile tile-options-menu"
      class:cover-photos-tile={user.show_cover_photos_for_groups}
      class:bookmarks={bookmarkId != null}
      class:mobile={mobile}
      class:spotlight-tile={showSpotlightTile}>
      <div class="top-bar" in:fade={{ duration: 100 }}>
        <button class="back-btn" on:click|preventDefault|stopPropagation={closeOptions}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0" mask-type="alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="12" height="12">
              <rect width="12" height="12" fill="white" />
            </mask>
            <g mask="url(#mask0)">
              <path
                d="M7.14978 6.08842C7.13814 6.07681 7.1289 6.06302 7.1226
              6.04783C7.1163 6.03265 7.11305 6.01637 7.11305 5.99993C7.11305
              5.98349 7.1163 5.96721 7.1226 5.95202C7.1289 5.93683 7.13814
              5.92304 7.14978 5.91143L11.7811 1.28062C11.9217 1.13979 12.0006
              0.948872 12.0005 0.749857C12.0004 0.550842 11.9212 0.360034
              11.7803 0.219408C11.6395 0.0787831 11.4486 -0.000140457 11.2496
              1.87653e-07C11.0506 0.000140832 10.8598 0.0793341 10.7191
              0.220158L6.08832 4.84897C6.07671 4.86061 6.06292 4.86985 6.04773
              4.87615C6.03254 4.88245 6.01626 4.8857 5.99982 4.8857C5.98338
              4.8857 5.9671 4.88245 5.95192 4.87615C5.93673 4.86985 5.92294
              4.86061 5.91133 4.84897L1.28051 0.220158C1.21088 0.150495 1.12821
              0.0952284 1.03722 0.0575143C0.946233 0.0198001 0.848706
              0.000376929 0.75021 0.000353716C0.551289 0.000306834 0.360496
              0.079283 0.219805 0.219908C0.079113 0.360534 4.69031e-05 0.551289
              2.08609e-08 0.75021C-4.68614e-05 0.949132 0.0789293 1.13992
              0.219555 1.28062L4.84987 5.91143C4.86151 5.92304 4.87074 5.93683
              4.87705 5.95202C4.88335 5.96721 4.88659 5.98349 4.88659
              5.99993C4.88659 6.01637 4.88335 6.03265 4.87705 6.04783C4.87074
              6.06302 4.86151 6.07681 4.84987 6.08842L0.219555 10.7197C0.149924
              10.7894 0.0946965 10.8721 0.0570253 10.9631C0.019354 11.0541
              -2.31929e-05 11.1516 2.08609e-08 11.2501C4.69031e-05 11.4491
              0.079113 11.6398 0.219805 11.7804C0.289468 11.8501 0.372164
              11.9053 0.463171 11.943C0.554179 11.9806 0.651715 12 0.75021
              12C0.949132 12 1.13989 11.9209 1.28051 11.7802L5.91133
              7.14888C5.92294 7.13724 5.93673 7.12801 5.95192 7.1217C5.9671
              7.1154 5.98338 7.11216 5.99982 7.11216C6.01626 7.11216 6.03254
              7.1154 6.04773 7.1217C6.06292 7.12801 6.07671 7.13724 6.08832
              7.14888L10.7191 11.7802C10.8598 11.9209 11.0505 12 11.2494
              12C11.4484 12 11.6391 11.9211 11.7798 11.7804C11.9205 11.6398
              11.9996 11.4491 11.9996 11.2501C11.9997 11.0512 11.9207 10.8604
              11.7801 10.7197L7.14978 6.08842Z"
                fill="black" />
            </g>
          </svg>
        </button>
      </div>

      <div class="menu-options">
        {#if bookmarkId == null}
          <div class="menu-options-view-list">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={(e) => {
                tileClicked(e);
              }}>
              View list
            </a>
          </div>
          {#if editable}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showListSettingsModal();
                  dispatch('list-settings-clicked');
                }}>
                List settings
              </a>
            </div>
            {#if user.show_cover_photos_for_groups}
              <div class="menu-options-members">
                <a
                  href="#"
                  on:click|preventDefault|stopPropagation={() => {
                    displayCoverPhotosModal();
                  }}>
                  Edit cover photos
                </a>
              </div>
            {/if}
            <div class="menu-options-duplicate">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showDuplicateListModal = true;
                  closeOptions();
                }}>
                Duplicate list
              </a>
            </div>
          {/if}
          <div class="menu-options-members">
            {#if editable}
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  displayShareModal();
                }}>
                Share list
              </a>
            {:else}
              <ListCopier
                group={group}
                mobile={mobile}
                relatedUserId={owner.id}
                on:link-copied={() => {
                  showOptionsMenu = false;
                }}>
                <span class="link"> Share list </span>
              </ListCopier>
            {/if}
          </div>
          {#if user.id === currentUser.id && groupCollections.length > 1 && !mobile}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showMoveToCollectionModal();
                  dispatch('move-to-collection');
                }}>
                Move to collection
              </a>
            </div>
          {/if}
          {#if user.id !== currentUser.id}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showOptionsMenu = false;
                  reportContentStore.set({ show: true, params: { group_base_id: group.id }, owner: user });
                }}>
                Report list
              </a>
            </div>
          {/if}
          {#if user.id === currentUser.id && mobile}
            <div class="menu-options-members">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  goto('/profiles/list_organizer');
                }}>
                Organize lists
              </a>
            </div>
          {/if}
          {#if editable}
            <div class="menu-options-delete">
              <a
                href="#"
                on:click|preventDefault|stopPropagation={() => {
                  showOptionsMenu = false;
                  showDeleteModal = true;
                  dispatch('delete-menu-clicked');
                }}>
                Delete
              </a>
            </div>
          {/if}
        {:else}
          <div class="menu-options-members">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={() => {
                displayMoveToBookmarkCollectionModal();
              }}>
              Add to folder
            </a>
          </div>
          <div class="menu-options-delete">
            <a
              href="#"
              on:click|preventDefault|stopPropagation={() => {
                displayRemoveBookmarkModal();
              }}>
              Remove
            </a>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if editable && showEmoji}
    <div
      class="backdrop-darker"
      on:click|preventDefault|stopPropagation={closeEmojiSelector}
      in:fade={{ duration: 100 }} />

    <div in:fade={{ duration: 100 }} class="emoji-container">
      <EmojiPicker
        mobile={mobile}
        on:emoji-selected={(e) => {
          closeEmojiSelector();
          selectEmoji(e.detail.emoji);
        }} />
    </div>
  {/if}

  <a href={group.path} style="display: contents;" data-sveltekit-preload-data>
    <div
      class="tile draggable-tile"
      class:cover-photos-tile={user.show_cover_photos_for_groups}
      class:mobile={mobile}
      on:click|preventDefault|stopPropagation={(e) => {
        tileClicked(e);
      }}>
      <div class="top-bar" in:fade={{ duration: 100 }}>
        <div class="left-side">
          {#if group.optimized_score_number}
            <ScoreStatusBadge
              score={group.optimized_score_number}
              mobile={mobile}
              currentUser={currentUser}
              group={group} />
          {/if}
        </div>
        <div class="right-side">
          <div
            class="emoji undraggable"
            class:editable={editable && !mobile}
            on:click|preventDefault|stopPropagation={openEmojiSelector}>
            <div class="icon">
              <VisibilityGuard let:hasBeenVisible>
                {#if hasBeenVisible}
                  <img src={group.emoji.outside_src} alt="List Emoji" />
                {/if}
              </VisibilityGuard>
            </div>
          </div>

          <div class="tile-options" in:fade={{ duration: 100 }}>
            {#if showWelcomeNoteFlag && browser}
              <WelcomeNoteActiveIndicator
                location={user.show_cover_photos_for_groups ? 'profileCoverPhotos' : 'profileNew'}
                groupId={id}
                mobile={mobile} />
            {/if}
            {#if currentUser && currentUser.id && isPrivate}
              <button
                class="tile-btn"
                use:setupTippy
                data-tippy-content="Link sharing is off"
                on:click|preventDefault|stopPropagation={goToPrivacySettings}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 1.99976C12.807 1.99995 15.083 4.27573 15.083 7.08276V8.67651H16.1309L16.2334 8.6814C16.7376 8.73264 17.1309 9.15877 17.1309 9.67651V17.0007L17.126 17.1033C17.0778 17.5735 16.7038 17.9481 16.2334 17.9958L16.1309 18.0007H3.86816L3.76562 17.9958C3.29537 17.948 2.92117 17.5735 2.87305 17.1033L2.86816 17.0007V9.67651C2.86816 9.15885 3.26151 8.73275 3.76562 8.6814L3.86816 8.67651H4.91699V7.08276C4.91699 4.27561 7.19285 1.99976 10 1.99976ZM4.36816 16.5007H15.6309V10.1765H4.36816V16.5007ZM10 10.99C11.231 10.9902 12.2285 11.9885 12.2285 13.2195C12.2282 14.4502 11.2308 15.4478 10 15.448C8.76906 15.448 7.77083 14.4503 7.77051 13.2195C7.77051 11.9883 8.76886 10.99 10 10.99ZM10 12.49C9.59729 12.49 9.27051 12.8168 9.27051 13.2195C9.27083 13.6219 9.59749 13.948 10 13.948C10.4023 13.9478 10.7282 13.6218 10.7285 13.2195C10.7285 12.8169 10.4025 12.4902 10 12.49ZM10 3.49976C8.02128 3.49976 6.41699 5.10404 6.41699 7.08276V8.67651H13.583V7.08276C13.583 5.10416 11.9786 3.49995 10 3.49976Z"
                    fill="#1A1A1A" />
                </svg>
              </button>
            {/if}

            {#if currentUser && currentUser.id && isHidden}
              <button
                class="tile-btn"
                use:setupTippy
                data-tippy-content="This list is hidden"
                on:click|preventDefault|stopPropagation={goToPrivacySettings}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M17.8984 3.18237L3.20312 17.8777L2.14258 16.8171L5.11816 13.8406C4.86072 13.6567 4.61726 13.4675 4.39062 13.2761C3.693 12.6871 3.12283 12.0661 2.7207 11.5281C2.52036 11.26 2.35297 11.001 2.23242 10.7673C2.1416 10.5912 2.03353 10.3483 2.00781 10.0945L2.00195 9.98511L2.00781 9.87573C2.0335 9.6216 2.14152 9.37813 2.23242 9.2019C2.35302 8.96817 2.52031 8.7093 2.7207 8.44116C3.1228 7.90318 3.69313 7.28298 4.39062 6.69409C5.77305 5.52703 7.75242 4.40308 10 4.40308C11.3188 4.40316 12.5449 4.79061 13.6123 5.34644L16.8379 2.12183L17.8984 3.18237ZM16.0752 7.10913C16.5629 7.56717 16.9699 8.02856 17.2783 8.44116C17.4787 8.7093 17.646 8.96817 17.7666 9.2019C17.8704 9.40322 17.997 9.69242 17.9971 9.98511C17.997 10.2776 17.8704 10.566 17.7666 10.7673C17.6461 11.001 17.4787 11.2599 17.2783 11.5281C16.8762 12.0661 16.306 12.6871 15.6084 13.2761C14.2261 14.4431 12.2474 15.566 10 15.5662C9.27885 15.5662 8.58572 15.4492 7.93066 15.2537L9.18359 14.0007C9.45241 14.0417 9.72466 14.0662 10 14.0662C11.7549 14.066 13.4001 13.177 14.6406 12.1296C15.2544 11.6114 15.7458 11.0739 16.0771 10.6306C16.2433 10.4083 16.3612 10.2203 16.4336 10.0798C16.454 10.0403 16.4655 10.0076 16.4756 9.98413C16.4655 9.96074 16.4537 9.92829 16.4336 9.8894C16.3611 9.74896 16.2432 9.56082 16.0771 9.33862C15.816 8.98925 15.4542 8.58242 15.0127 8.17163L16.0752 7.10913ZM10 5.90308C8.24505 5.90308 6.59904 6.79227 5.3584 7.8396C4.74468 8.35772 4.25321 8.89534 3.92188 9.33862C3.7558 9.56084 3.63792 9.74895 3.56543 9.8894C3.54526 9.9285 3.53252 9.96069 3.52246 9.98413C3.53253 10.0077 3.54496 10.0401 3.56543 10.0798C3.63787 10.2203 3.75575 10.4083 3.92188 10.6306C4.25318 11.0739 4.74463 11.6114 5.3584 12.1296C5.62004 12.3505 5.90032 12.5637 6.19531 12.7634L7.30957 11.6492C7.00877 11.1662 6.83403 10.5965 6.83398 9.98511C6.83415 8.23679 8.25164 6.81909 10 6.81909C10.6112 6.81918 11.1801 6.99493 11.6631 7.29565L12.4893 6.46948C11.7102 6.12217 10.8687 5.90315 10 5.90308ZM10 8.31909C9.08007 8.31909 8.33415 9.06521 8.33398 9.98511C8.33401 10.1767 8.36715 10.3607 8.42676 10.532L10.5469 8.41187C10.3756 8.35221 10.1917 8.31914 10 8.31909Z"
                    fill="#1A1A1A" />
                </svg>
              </button>
            {/if}

            {#if showDetailsButton || bookmarkId}
              <button class="tile-menu undraggable" on:click|preventDefault|stopPropagation={showOptions}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M3.77778 8.22217C2.8 8.22217 2 9.02217 2 9.99995C2 10.9777 2.8 11.7777 3.77778 11.7777C4.75556 11.7777 5.55556 10.9777 5.55556 9.99995C5.55556 9.02217 4.75556 8.22217 3.77778 8.22217ZM16.2222 8.22217C15.2444 8.22217 14.4444 9.02217 14.4444 9.99995C14.4444 10.9777 15.2444 11.7777 16.2222 11.7777C17.2 11.7777 18 10.9777 18 9.99995C18 9.02217 17.2 8.22217 16.2222 8.22217ZM10 8.22217C9.02222 8.22217 8.22222 9.02217 8.22222 9.99995C8.22222 10.9777 9.02222 11.7777 10 11.7777C10.9778 11.7777 11.7778 10.9777 11.7778 9.99995C11.7778 9.02217 10.9778 8.22217 10 8.22217Z"
                    fill="#1A1A1A" />
                </svg>
              </button>
            {/if}
          </div>
        </div>
      </div>

      <div class="main-content">
        {#if user.show_cover_photos_for_groups}
          <div class="image-container">
            {#if group.minified_cover_image}
              <img
                src={group.minified_cover_image}
                alt="{group.name} cover photo collage"
                style={mobile
                  ? 'width: 236px; height: 190px; border-radius: 10px;'
                  : 'height: 203px; width: 252px; border-radius: 10px;'} />
              <div class="image-subtle-overlay radius-10"></div>
            {:else}
              <TileCoverPhotos group={group} mobile={mobile} />
              {#if group.cover_photos.length > 0}
                <div class="image-subtle-overlay radius-12"></div>
              {/if}
            {/if}
          </div>
        {/if}

        <div class="group-name-container">
          <span class="group-name">
            <Text
              lines={2}
              maxHeight={58}
              text={editable
                ? group.name
                : `<h2 style='margin-top: 0px; margin-bottom: 0px; line-height: 1.4; font-size: ${
                    mobile ? 15 : 18
                  }px;' >${group.name}</h2>`}
              expand={false}
              editable={editable && !mobile && showDetailsButton}
              on:focus={() => {
                editingText = true;
              }}
              on:blur={async (e) => {
                setTimeout(() => {
                  editingText = false;
                }, 500);

                let data = {
                  name: e.detail.text,
                };

                try {
                  let resp = await GroupAjax.update(group, data);
                  group.set({ ...group, ...resp });
                } catch (err) {
                  if (err && err.full_errors.length > 0 && err.full_errors[0]) {
                    Notification.send({
                      text: err.full_errors[0],
                    });
                    group = group;
                  }
                }
              }} />
          </span>
        </div>

        <div class="group-metadata-container">
          {#if group && group.permissions && group.permissions.edit && group.broken_weblinks_count && group.broken_weblinks_count > 0}
            <img
              use:setupTippyForBrokenLink={group.path}
              src="/images/alert.svg"
              style="width: 16px; margin-top: 10px;" />
          {/if}
          {#if group.items_count}
            <div class="items-count {bookmarkId != null ? 'bookmarks' : ''}">
              {group.items_count}
              {group.items_count == 1 ? 'item' : 'items'}
            </div>
          {/if}
          {#if bookmarkId == null}
            <div class="authors">
              <ListAuthors
                nameMaxLinesShown={2}
                condensedView={true}
                group={group}
                showContributorModal={false}
                showContributorsTippyLabel={true}
                mobile={mobile}
                disableHyperlink={true}
                showNames={!mobile}
                contributorText={mobile ? '+[count]' : '+[remainingCount]'}
                authors={authors}
                skipBy={true} />
            </div>
          {/if}
        </div>
        {#if bookmarkId != null}
          <div class="groups-authors">
            <ListAuthors
              nameMaxLinesShown={1}
              group={group}
              showContributorModal={false}
              showContributorsTippyLabel={true}
              disableHyperlink={true}
              authors={authors}
              mobile={mobile}
              skipBy={true} />
          </div>
        {/if}

        {#if group.description && extractText(group.description) != ''}
          <div class="tile-group-description">{extractText(group.description)}</div>
        {/if}
      </div>

      <div class="tile-group-bottom-bar">
        <GroupActionsBar groupStore={group} group={group} displayStyle="tile" score={score} />
      </div>
    </div>
  </a>
  {#if reportedContentId}
    <HideReportedContentOverlay
      text="Show List"
      reportedContentId={reportedContentId}
      reportedContentKey={reportedContentKey} />
  {/if}
</div>
